import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  FileText, 
  Plus, 
  Send, 
  Eye,
  Download,
  Sparkles,
  FileSignature,
  Users,
  Building2,
  Briefcase,
  Search,
  Loader2,
  CheckCircle2,
  Server,
  Shield,
  ScrollText,
  Edit2,
  Save,
  Printer,
  Copy,
  RefreshCw,
  Info,
  Lock,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// =============================================
// CONTRACT GENERATOR PAGE - Template + Client + Service
// =============================================

const ContractGeneratorPage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Data
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  
  // Selection
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  
  // Generated contract
  const [generatedContent, setGeneratedContent] = useState('');
  const [generatedValues, setGeneratedValues] = useState({});
  const [customValues, setCustomValues] = useState({});
  
  // UI State
  const [step, setStep] = useState(1); // 1: Select template, 2: Select entities, 3: Preview/Edit, 4: Save
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, clientsRes, servicesRes, projectsRes, membersRes] = await Promise.all([
        api.get('/settings/contract-templates'),
        api.get('/users/', { params: { role: 'client' } }),
        api.get('/settings/third-party-services'),
        api.get('/projects/'),
        api.get('/users/team-members')
      ]);
      
      setTemplates(templatesRes.data || []);
      setClients(clientsRes.data || []);
      setServices(servicesRes.data || []);
      setProjects(projectsRes.data || []);
      setMembers(membersRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Init default templates if none exist
  const initDefaultTemplates = async () => {
    try {
      const res = await api.post('/settings/contract-templates/init-defaults');
      toast.success(`${res.data.created} modèles créés, ${res.data.updated} mis à jour`);
      loadData();
    } catch (error) {
      console.error('Error init templates:', error);
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  // Load schedules when project is selected
  useEffect(() => {
    if (selectedProject) {
      loadSchedulesForProject(selectedProject.id);
    }
  }, [selectedProject]);

  const loadSchedulesForProject = async (projectId) => {
    try {
      const res = await api.get('/settings/payment-schedules', { params: { project_id: projectId } });
      setSchedules(res.data || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  // Generate contract from template
  const handleGenerateContract = async () => {
    if (!selectedTemplate) {
      toast.error('Sélectionnez un modèle');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        template_id: selectedTemplate.id,
        client_id: selectedClient?.id || null,
        service_id: selectedService?.id || null,
        project_id: selectedProject?.id || null,
        member_id: selectedMember?.id || null,
        schedule_id: selectedSchedule?.id || null,
        custom_values: customValues
      };

      const res = await api.post('/settings/contract-templates/generate', payload);
      setGeneratedContent(res.data.content);
      setGeneratedValues(res.data.values);
      setStep(3);
      toast.success('Contrat généré');
    } catch (error) {
      console.error('Error generating:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  // Save contract
  const handleSaveContract = async () => {
    if (!selectedClient && !selectedMember) {
      toast.error('Sélectionnez un client ou un membre');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        template_id: selectedTemplate.id,
        template_name: selectedTemplate.name,
        type: selectedTemplate.type,
        content: generatedContent,
        client_id: selectedClient?.id || null,
        member_id: selectedMember?.id || null,
        service_id: selectedService?.id || null,
        project_id: selectedProject?.id || null,
        schedule_id: selectedSchedule?.id || null,
        values: generatedValues,
        status: 'draft'
      };

      await api.post('/contracts/from-template', payload);
      toast.success('Contrat enregistré');
      // Reset form
      setStep(1);
      setSelectedTemplate(null);
      setSelectedClient(null);
      setSelectedService(null);
      setSelectedProject(null);
      setSelectedMember(null);
      setSelectedSchedule(null);
      setGeneratedContent('');
      setGeneratedValues({});
      setCustomValues({});
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Template category badges
  const getCategoryBadge = (template) => {
    const categoryColors = {
      'service_tiers': 'bg-blue-100 text-blue-700',
      'projet': 'bg-green-100 text-green-700',
      'equipe': 'bg-purple-100 text-purple-700',
      'legal': 'bg-amber-100 text-amber-700'
    };
    const categoryLabels = {
      'service_tiers': 'Service Tiers',
      'projet': 'Projet',
      'equipe': 'Équipe',
      'legal': 'Juridique'
    };
    return (
      <Badge className={categoryColors[template.category] || 'bg-slate-100 text-slate-700'}>
        {categoryLabels[template.category] || template.category}
      </Badge>
    );
  };

  // Check if service selection is locked (for hosting/maintenance contracts)
  const isServiceLocked = selectedTemplate?.linked_to_service_type;
  const filteredServices = isServiceLocked 
    ? services.filter(s => s.category === selectedTemplate.linked_to_service_type || s.type === selectedTemplate.linked_to_service_type)
    : services;

  // Determine what entities are needed based on template type
  const needsClient = ['hosting', 'maintenance', 'project', 'payment_schedule', 'cgv'].includes(selectedTemplate?.type);
  const needsService = ['hosting', 'maintenance'].includes(selectedTemplate?.type);
  const needsProject = ['project', 'payment_schedule'].includes(selectedTemplate?.type);
  const needsMember = ['member', 'subcontractor'].includes(selectedTemplate?.type);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Générateur de Contrats</h1>
            <p className="text-slate-500 mt-1">
              Créez des contrats pré-remplis à partir de modèles
            </p>
          </div>
          {templates.length === 0 && (
            <Button onClick={initDefaultTemplates}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Initialiser les modèles
            </Button>
          )}
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2">
          {[
            { num: 1, label: 'Modèle', icon: ScrollText },
            { num: 2, label: 'Données', icon: Users },
            { num: 3, label: 'Aperçu', icon: Eye },
            { num: 4, label: 'Enregistrer', icon: Save }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <button
                onClick={() => s.num < step && setStep(s.num)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-all",
                  step === s.num 
                    ? "bg-slate-900 text-white" 
                    : step > s.num 
                      ? "bg-green-100 text-green-700 hover:bg-green-200" 
                      : "bg-slate-100 text-slate-400"
                )}
                disabled={s.num > step}
              >
                {step > s.num ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <s.icon className="w-4 h-4" />
                )}
                <span className="font-medium">{s.label}</span>
              </button>
              {idx < 3 && (
                <div className={cn(
                  "flex-1 h-0.5",
                  step > s.num ? "bg-green-300" : "bg-slate-200"
                )} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Template */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Sélectionnez un modèle de contrat</h2>
            
            {templates.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <ScrollText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 mb-4">Aucun modèle de contrat disponible</p>
                  <Button onClick={initDefaultTemplates}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer les modèles par défaut
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.filter(t => t.is_active !== false).map((template) => (
                  <Card 
                    key={template.id}
                    className={cn(
                      "cursor-pointer transition-all hover:shadow-md",
                      selectedTemplate?.id === template.id && "ring-2 ring-slate-900"
                    )}
                    onClick={() => setSelectedTemplate(template)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-base">{template.name}</CardTitle>
                          <CardDescription className="text-sm mt-1">
                            {template.description}
                          </CardDescription>
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getCategoryBadge(template)}
                        {template.linked_to_service_type && (
                          <Badge variant="outline" className="text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            Lié à {template.linked_to_service_type}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        {template.variables?.length || 0} variables
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <Button 
                onClick={() => setStep(2)} 
                disabled={!selectedTemplate}
              >
                Continuer
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select Entities */}
        {step === 2 && selectedTemplate && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Sélectionnez les données</h2>
              <Badge>{selectedTemplate.name}</Badge>
            </div>

            {/* Alert for locked services */}
            {isServiceLocked && (
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-700">
                  Ce modèle est lié aux services de type "{selectedTemplate.linked_to_service_type}". 
                  Le montant sera automatiquement récupéré depuis le service sélectionné.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              {/* Client selection */}
              {needsClient && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Client
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select 
                      value={selectedClient?.id || ''} 
                      onValueChange={(v) => {
                        const client = clients.find(c => c.id === v);
                        setSelectedClient(client);
                        // Load projects for client
                        if (client) {
                          api.get('/projects/', { params: { client_id: client.id } })
                            .then(res => setProjects(res.data || []));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un client..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span>{client.company_name || `${client.first_name} ${client.last_name}`}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedClient && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                        <p className="font-medium">{selectedClient.company_name}</p>
                        <p className="text-slate-500">{selectedClient.email}</p>
                        {selectedClient.siret && (
                          <p className="text-slate-500">SIRET: {selectedClient.siret}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Member selection */}
              {needsMember && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Membre / Prestataire
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select 
                      value={selectedMember?.id || ''} 
                      onValueChange={(v) => setSelectedMember(members.find(m => m.id === v))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un membre..." />
                      </SelectTrigger>
                      <SelectContent>
                        {members.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            <div className="flex items-center gap-2">
                              <span>{member.first_name} {member.last_name}</span>
                              <Badge variant="outline" className="text-xs">{member.role}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedMember && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                        <p className="font-medium">{selectedMember.first_name} {selectedMember.last_name}</p>
                        <p className="text-slate-500">{selectedMember.email}</p>
                        {selectedMember.siret && (
                          <Badge className="mt-1 bg-green-100 text-green-700">SIRET: {selectedMember.siret}</Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Service selection */}
              {needsService && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Server className="w-4 h-4" />
                      Service Tiers
                      {isServiceLocked && (
                        <Lock className="w-3 h-3 text-amber-500" />
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select 
                      value={selectedService?.id || ''} 
                      onValueChange={(v) => setSelectedService(services.find(s => s.id === v))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un service..." />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredServices.map((service) => (
                          <SelectItem key={service.id} value={service.id}>
                            <div className="flex items-center gap-2">
                              <span>{service.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {service.sale_price}€/{service.billing_type === 'monthly' ? 'mois' : 'an'}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedService && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                        <p className="font-medium">{selectedService.name}</p>
                        <p className="text-slate-500">{selectedService.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-blue-100 text-blue-700">
                            {selectedService.sale_price}€ HT/{selectedService.billing_type === 'monthly' ? 'mois' : 'an'}
                          </Badge>
                          {isServiceLocked && (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              Montant verrouillé
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Project selection */}
              {needsProject && selectedClient && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Projet
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select 
                      value={selectedProject?.id || ''} 
                      onValueChange={(v) => setSelectedProject(projects.find(p => p.id === v))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un projet..." />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.filter(p => p.client_id === selectedClient?.id).map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            <span>{project.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedProject && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm">
                        <p className="font-medium">{selectedProject.name}</p>
                        <p className="text-slate-500">{selectedProject.description}</p>
                        <Badge className="mt-1 bg-green-100 text-green-700">
                          Budget: {selectedProject.budget}€
                        </Badge>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Payment Schedule selection */}
              {selectedTemplate?.type === 'payment_schedule' && selectedProject && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Échéancier
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select 
                      value={selectedSchedule?.id || ''} 
                      onValueChange={(v) => setSelectedSchedule(schedules.find(s => s.id === v))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un échéancier..." />
                      </SelectTrigger>
                      <SelectContent>
                        {schedules.map((schedule) => (
                          <SelectItem key={schedule.id} value={schedule.id}>
                            <span>{schedule.name || `Échéancier ${schedule.id.slice(-4)}`}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Custom values for missing variables */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Valeurs complémentaires</CardTitle>
                <CardDescription>
                  Renseignez les informations manquantes (optionnel)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Durée du contrat</Label>
                    <Input 
                      value={customValues.contract_duration || ''}
                      onChange={(e) => setCustomValues({...customValues, contract_duration: e.target.value})}
                      placeholder="12 mois"
                    />
                  </div>
                  <div>
                    <Label>Préavis de résiliation</Label>
                    <Input 
                      value={customValues.notice_period || ''}
                      onChange={(e) => setCustomValues({...customValues, notice_period: e.target.value})}
                      placeholder="1 mois"
                    />
                  </div>
                  <div>
                    <Label>Date de début</Label>
                    <Input 
                      type="date"
                      value={customValues.start_date || ''}
                      onChange={(e) => setCustomValues({...customValues, start_date: e.target.value})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Retour
              </Button>
              <Button 
                onClick={handleGenerateContract}
                disabled={generating || (needsClient && !selectedClient) || (needsMember && !selectedMember) || (needsService && !selectedService)}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Générer le contrat
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preview & Edit */}
        {step === 3 && generatedContent && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Aperçu du contrat</h2>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedContent);
                    toast.success('Copié');
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copier
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.print()}
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimer
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-6">
                <Textarea
                  value={generatedContent}
                  onChange={(e) => setGeneratedContent(e.target.value)}
                  className="min-h-[600px] font-mono text-sm whitespace-pre-wrap"
                />
              </CardContent>
            </Card>

            {/* Variables still empty */}
            {generatedContent.includes('{{') && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  Certaines variables n'ont pas été remplies. Vous pouvez les modifier manuellement dans le texte.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                Retour
              </Button>
              <Button onClick={() => setStep(4)}>
                Continuer
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Save */}
        {step === 4 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Enregistrer le contrat</h2>

            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Modèle utilisé</Label>
                    <Input value={selectedTemplate?.name} disabled className="bg-slate-50" />
                  </div>
                  {selectedClient && (
                    <div>
                      <Label>Client</Label>
                      <Input 
                        value={selectedClient.company_name || `${selectedClient.first_name} ${selectedClient.last_name}`} 
                        disabled 
                        className="bg-slate-50" 
                      />
                    </div>
                  )}
                  {selectedMember && (
                    <div>
                      <Label>Membre</Label>
                      <Input 
                        value={`${selectedMember.first_name} ${selectedMember.last_name}`} 
                        disabled 
                        className="bg-slate-50" 
                      />
                    </div>
                  )}
                  {selectedService && (
                    <div>
                      <Label>Service</Label>
                      <Input 
                        value={`${selectedService.name} - ${selectedService.sale_price}€`} 
                        disabled 
                        className="bg-slate-50" 
                      />
                    </div>
                  )}
                  {selectedProject && (
                    <div>
                      <Label>Projet</Label>
                      <Input value={selectedProject.name} disabled className="bg-slate-50" />
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">
                    Le contrat sera enregistré en statut <Badge>Brouillon</Badge> et pourra être envoyé pour signature.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}>
                Retour
              </Button>
              <Button onClick={handleSaveContract} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer le contrat
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ContractGeneratorPage;
